import React, { useState, useMemo, useContext } from "react";
import { Expert } from "@hooks/useGetExpert";
import styled from "styled-components";
import { OnboardingContext } from "@context/onboardingContext";
import ExpertCards from "./ExpertCards";
import mediaQuery from "@utils/mediaQuery";

const Container = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: var(--font-size-3xl);
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 26px;
  margin-bottom: 24px;
  color: #1a365d;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  padding: 20px;
  background-color: var(--bg-input);
  border-radius: 8px;
  border: 1px solid var(--border-default);
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;

  ${mediaQuery("tablet")`
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  `}
`;

const FilterLabel = styled.label`
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  min-width: 80px;
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  font-size: var(--font-size-xl);
  background-color: var(--bg-card);
  min-width: 250px;
  cursor: pointer;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--interactive-secondary);
    box-shadow: 0 0 0 3px var(--shadow-focus);
  }

  &:disabled {
    background-color: #f7fafc;
    color: #a0aec0;
    cursor: not-allowed;
  }

  ${mediaQuery("tablet")`
    min-width: 100%;
  `}
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 12px 16px;
  height: 44px;
  background-color: ${props => props.$primary ? 'var(--interactive-secondary)' : 'var(--color-neutral-150)'};
  color: ${props => props.$primary ? 'var(--text-inverse)' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$primary ? 'var(--interactive-secondary)' : 'var(--border-default)'};
  border-radius: 8px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  font-family: inherit;

  &:hover:not(:disabled) {
    background-color: ${props => props.$primary ? 'var(--interactive-secondary-hover)' : '#cbd5e0'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  padding: 12px 16px;
  height: 44px;
  border: 1px solid var(--border-default);
  background-color: ${props => props.$active ? 'var(--interactive-secondary)' : 'var(--bg-card)'};
  color: ${props => props.$active ? 'var(--text-inverse)' : 'var(--text-secondary)'};
  border-radius: 8px;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  font-family: inherit;
  flex: 1;

  &:hover {
    background-color: ${props => props.$active ? 'var(--interactive-secondary-hover)' : 'var(--bg-input)'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px var(--shadow-focus);
  }

  ${mediaQuery("tablet")`
    flex: 1;
  `}
`;

const ResultsCount = styled.div`
  font-size: var(--font-size-base);
  color: var(--text-secondary);
  margin-bottom: 16px;
  font-style: italic;
  line-height: var(--line-height-base);
`;

const NoResultsMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
  font-style: italic;
  background-color: var(--bg-input);
  border-radius: 8px;
  border: 1px solid var(--border-default);
  font-size: var(--font-size-xl);
  line-height: var(--line-height-xl);
`;

const Section = styled.div`
  margin-bottom: 24px;
`;


type ViewMode = 'filtered' | 'all';

interface ExpertsSectionProps {
  title: string;
  experts?: {
    [districtName: string]: Expert[];
  };
}

const ExpertsSection: React.FC<ExpertsSectionProps> = ({ 
  title, 
  experts = {} 
}) => {
  // Get user's district from onboarding context
  const { userData } = useContext(OnboardingContext) || {};
  const userDistrict = userData?.district;

  const [selectedDistrict, setSelectedDistrict] = useState<string>(userDistrict || 'all');
  const [viewMode, setViewMode] = useState<ViewMode>(userDistrict ? 'filtered' : 'all');

  // Extract unique districts and sort them
  const districts = useMemo(() => {
    return Object.keys(experts).sort();
  }, [experts]);

  // Get filtered experts based on selection
  const filteredExperts = useMemo(() => {
    if (viewMode === 'all' || selectedDistrict === 'all') {
      return experts;
    }

    if (!selectedDistrict) {
      return {};
    }

    return {
      [selectedDistrict]: experts[selectedDistrict] || []
    };
  }, [experts, selectedDistrict, viewMode]);

  // Calculate total experts count
  const totalExpertsCount = useMemo(() => {
    return Object.values(filteredExperts).reduce(
      (total, regionExperts) => total + (regionExperts?.length || 0), 
      0
    );
  }, [filteredExperts]);

  const handleDistrictChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedDistrict(event.target.value);
    if (event.target.value === 'all') {
      setViewMode('all');
    } else {
      setViewMode('filtered');
    }
  };


  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'all') {
      setSelectedDistrict('all');
    } else if (mode === 'filtered' && userDistrict) {
      setSelectedDistrict(userDistrict);
    }
  };


  return (
    <Container id={title}>
      <Title>{title}</Title>
      
      <FilterContainer>
        <FilterRow>
          <FilterLabel>District:</FilterLabel>
          <Select
            value={selectedDistrict}
            onChange={handleDistrictChange}
          >
            <option value="all">All districts</option>
            {districts.map((district) => (
              <option key={district} value={district}>
                {district}
                {district === userDistrict && ' (Your District)'}
              </option>
            ))}
          </Select>
          

          <ViewToggle>
            <ToggleButton
              $active={viewMode === 'filtered'}
              onClick={() => handleViewModeChange('filtered')}
            >
              {userDistrict ? userDistrict : 'My District'}
            </ToggleButton>
            <ToggleButton
              $active={viewMode === 'all'}
              onClick={() => handleViewModeChange('all')}
            >
              Show All
            </ToggleButton>
          </ViewToggle>
        </FilterRow>
      </FilterContainer>

      {totalExpertsCount > 0 && (
        <ResultsCount>
          {viewMode === 'all'
            ? `Showing all experts (${totalExpertsCount} total across ${Object.keys(filteredExperts).length} districts)`
            : selectedDistrict && selectedDistrict !== 'all'
              ? `Showing ${totalExpertsCount} expert${totalExpertsCount !== 1 ? 's' : ''} in ${selectedDistrict}${selectedDistrict === userDistrict ? ' (your district)' : ''}`
              : `${totalExpertsCount} expert${totalExpertsCount !== 1 ? 's' : ''} available`
          }
        </ResultsCount>
      )}

      {totalExpertsCount === 0 ? (
        <NoResultsMessage>
          {selectedDistrict && selectedDistrict !== 'all'
            ? `No experts found in ${selectedDistrict}. Try selecting a different district or view all experts.`
            : 'No experts available. Please try again later.'
          }
        </NoResultsMessage>
      ) : (
        Object.keys(filteredExperts).map((district) => (
          <Section key={district}>
            <ExpertCards
              title={district}
              experts={filteredExperts[district]}
            />
          </Section>
        ))
      )}
    </Container>
  );
};

export default ExpertsSection;